import React, { useState, useEffect } from 'react';
import { PauseCircle, X, Clock } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const PAUSE_REASONS = [
  'Rain Delay',
  'Injury Timeout',
  'Break',
  'Equipment Issue',
  'Other'
];

const DURATION_OPTIONS = [
  { label: '5 min', value: 5 },
  { label: '10 min', value: 10 },
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: 'Indefinite', value: null }
];

const PauseGameModal = ({ isOpen, onClose, onConfirm, loading = false }) => {
  const { theme } = useTheme();
  const [reason, setReason] = useState('Break');
  const [customReason, setCustomReason] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(15);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    const finalReason = reason === 'Other' ? customReason || 'Other' : reason;
    onConfirm({
      reason: finalReason,
      durationMinutes: durationMinutes
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className={`relative w-full max-w-sm ${theme.card} rounded-2xl shadow-xl overflow-hidden`}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
              <PauseCircle className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className={`text-lg font-semibold ${theme.text}`}>Pause Game</h3>
          </div>
          <button onClick={onClose} className={`p-1 rounded-full ${theme.secondaryHover}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Reason */}
          <div>
            <label className={`text-sm font-medium ${theme.textSecondary} mb-2 block`}>Reason</label>
            <div className="flex flex-wrap gap-2">
              {PAUSE_REASONS.map(r => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                    ${reason === r
                      ? 'bg-amber-500 text-white'
                      : `${theme.secondary} ${theme.text} ${theme.secondaryHover}`
                    }`}
                >
                  {r}
                </button>
              ))}
            </div>
            {reason === 'Other' && (
              <input
                type="text"
                value={customReason}
                onChange={e => setCustomReason(e.target.value)}
                placeholder="Enter reason..."
                className={`mt-2 w-full px-3 py-2 rounded-lg border ${theme.secondary} ${theme.text} text-sm focus:outline-none focus:ring-2 focus:ring-amber-500`}
                autoFocus
              />
            )}
          </div>

          {/* Duration */}
          <div>
            <label className={`text-sm font-medium ${theme.textSecondary} mb-2 flex items-center gap-1`}>
              <Clock className="w-4 h-4" /> Duration
            </label>
            <div className="flex flex-wrap gap-2">
              {DURATION_OPTIONS.map(opt => (
                <button
                  key={opt.label}
                  onClick={() => setDurationMinutes(opt.value)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                    ${durationMinutes === opt.value
                      ? 'bg-amber-500 text-white'
                      : `${theme.secondary} ${theme.text} ${theme.secondaryHover}`
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={loading}
            className={`flex-1 py-2.5 rounded-lg font-medium ${theme.secondary} ${theme.text} ${theme.secondaryHover}`}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || (reason === 'Other' && !customReason.trim())}
            className="flex-1 py-2.5 rounded-lg font-medium bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? 'Pausing...' : (
              <>
                <PauseCircle className="w-4 h-4" />
                Pause Game
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PauseGameModal;
